import { Button, cn } from "@tellescope/design-system";
import { ExpandableDialogWithSidebar, ExpandableDialogWithSidebarProps, useUncontrolledState } from "@tellescope/ui";
import {
  Activity,
  BookOpen,
  Calendar,
  ClipboardList,
  Clock,
  CreditCard,
  FileText,
  Package,
  Pill,
  ShieldCheck,
  Ticket,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSelectContactDetailsBlockDialog } from "./useSelectContactDetailsBlockDialog";
import { useTranslation } from "react-i18next";
import { BlockProps, ContactDetailsBlockType } from "../../types";
import { BlockSelectionGrid } from "./BlockSelectionGrid";
import { SplitStringType } from "@tellescope/type-utils";
import { CONTACT_DETAILS_BLOCKS_FLAT_MAP } from "../../constants";
import { i18n } from "@tellescope/i18n";
import { useBlockAvailability } from "../../hooks";

type BlockSection = SplitStringType<ContactDetailsBlockType, ".">[0];

/**
 * The dialog's sidebar sections, in display order. Titles are getters so the literal stays inside a
 * static `t()` call for extraction while still resolving at render time.
 */
const BLOCK_SECTIONS: readonly { sectionKey: BlockSection; getTitle: () => string; icon: LucideIcon }[] = [
  { sectionKey: "information", getTitle: () => i18n.t("Information", { ns: "contacts" }), icon: UserRound },
  { sectionKey: "related-contacts", getTitle: () => i18n.t("Related Contacts", { ns: "contacts" }), icon: Users },
  { sectionKey: "timeline", getTitle: () => i18n.t("Timeline", { ns: "contacts" }), icon: Clock },
  { sectionKey: "tickets", getTitle: () => i18n.t("Tickets", { ns: "contacts" }), icon: Ticket },
  { sectionKey: "calendar", getTitle: () => i18n.t("Calendar", { ns: "contacts" }), icon: Calendar },
  { sectionKey: "eligibility", getTitle: () => i18n.t("Eligibility", { ns: "contacts" }), icon: ShieldCheck },
  { sectionKey: "orders", getTitle: () => i18n.t("Orders", { ns: "contacts" }), icon: Package },
  { sectionKey: "files", getTitle: () => i18n.t("Files", { ns: "contacts" }), icon: FileText },
  { sectionKey: "forms", getTitle: () => i18n.t("Forms", { ns: "contacts" }), icon: ClipboardList },
  { sectionKey: "content", getTitle: () => i18n.t("Content", { ns: "contacts" }), icon: BookOpen },
  { sectionKey: "medications", getTitle: () => i18n.t("Medications", { ns: "contacts" }), icon: Pill },
  { sectionKey: "payments", getTitle: () => i18n.t("Payments", { ns: "contacts" }), icon: CreditCard },
  { sectionKey: "vitals", getTitle: () => i18n.t("Vitals", { ns: "contacts" }), icon: Activity },
];

export interface SelectContactDetailsBlockDialogProps
  extends Omit<ExpandableDialogWithSidebarProps, "title" | "children" | "selectedSection" | "setSelectedSection"> {
  viewId: string;
  isFeatureEnabled?: BlockProps["isFeatureEnabled"];
  onViewMaterialized?: BlockProps["onViewMaterialized"];
  onBlocksAdded?: (blockTypes: ContactDetailsBlockType[]) => void;
}

export const SelectContactDetailsBlockDialog = ({
  className,
  open: providedOpen,
  setOpen: providedSetOpen,
  viewId,
  isFeatureEnabled,
  onViewMaterialized,
  onBlocksAdded,
  ...rest
}: SelectContactDetailsBlockDialogProps) => {
  const { t } = useTranslation("contacts");
  const { isAvailable } = useBlockAvailability({ isFeatureEnabled });
  const { selectedBlocks, existingBlockCounts, isAddingBlocks, toggleBlock, clearSelection, addSelectedBlocks } =
    useSelectContactDetailsBlockDialog({ viewId, onViewMaterialized, onBlocksAdded });
  const [selectedSection, setSelectedSection] = useState("information");
  const [open, setOpen] = useUncontrolledState({
    state: providedOpen ?? false,
    setState: providedSetOpen,
  });

  const blockTypeNames = useMemo(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    () => Object.keys(CONTACT_DETAILS_BLOCKS_FLAT_MAP) as (keyof typeof CONTACT_DETAILS_BLOCKS_FLAT_MAP)[],
    []
  );
  const getSectionBlockTypeNames = useCallback(
    (section: SplitStringType<ContactDetailsBlockType, ".">[0]): ContactDetailsBlockType[] => {
      return blockTypeNames.filter((type) => type.startsWith(section) && isAvailable(type));
    },
    [blockTypeNames, isAvailable]
  );

  return (
    <ExpandableDialogWithSidebar
      title={t("Add a section")}
      selectedSection={selectedSection}
      setSelectedSection={setSelectedSection}
      className={cn("relative", className)}
      open={open}
      setOpen={setOpen}
      contentChildren={
        selectedBlocks.length ? (
          <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center bg-white rounded-lg p-2 shadow-md border border-secondary">
            <p className="text-sm font-semibold">{t("Add {{count}} blocks", { count: selectedBlocks.length })}</p>
            <span className="flex items-center gap-2">
              <Button variant="outline" onClick={clearSelection} size="sm">
                {t("Cancel")}
              </Button>
              <Button disabled={isAddingBlocks} onClick={addSelectedBlocks} size="sm">
                {t("Save")}
              </Button>
            </span>
          </div>
        ) : undefined
      }
      {...rest}
    >
      {BLOCK_SECTIONS.map(({ sectionKey, getTitle, icon }) => {
        const displayedBlockTypes = getSectionBlockTypeNames(sectionKey);
        if (!displayedBlockTypes.length) return null;

        return (
          <ExpandableDialogWithSidebar.Section key={sectionKey} sectionKey={sectionKey} title={getTitle()} icon={icon}>
            <BlockSelectionGrid
              displayedBlockTypes={displayedBlockTypes}
              selectedBlockTypes={selectedBlocks}
              existingBlockCounts={existingBlockCounts}
              onToggleBlock={toggleBlock}
            />
          </ExpandableDialogWithSidebar.Section>
        );
      })}
    </ExpandableDialogWithSidebar>
  );
};
